import { FC, ReactNode } from "react";
import { X, Check, AlertTriangle } from "lucide-react";
 import { ReactComponent as CheckIcon } from "../../assets/img/checkIcon2.svg";
import { ReactComponent as ErrorCancelIcon } from "../../assets/img/error-cancel-icon.svg";
import Button from "../UIKits/Button/Button";
import { Body } from "../UIKits/Typography/Typography";
 
type StatusType = "success" | "error" | "warning";

interface AlertBoxProps {
  title: string;
  message: string;
  linkText?: string;
  linkHref?: string;
  onLinkClick?: () => void;
}

export interface PaymentStatusCardProps {
  children: ReactNode;
  status: StatusType;
  title?: string;
  message?: string | ReactNode;
  alertBox?: AlertBoxProps;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  onClose: () => void;
  showCloseIcon?: boolean;
  showActions?: boolean;
  className?: string;
}


export const PaymentStatusCard: FC<PaymentStatusCardProps> = ({
  children,
  status,
  title,
  message,
  alertBox,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  onClose,
  showCloseIcon = true,
  showActions = true,
  className = "",
}) => {
  const statusConfig = {
    success: {
      iconBg: "bg-green-100",
      iconColor: "text-green-500",
      icon: CheckIcon,
    },
    error: {
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      icon: ErrorCancelIcon,
    },
    warning: {
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-600",
      icon: AlertTriangle,
    },
  };

  const config = statusConfig[status];
  const IconComponent = config.icon;

  return (
    <div className="flex min-h-60    flex-col items-center text-center mt-20">
      <div className="absolute top-10   z-5">
        <IconComponent className={`w-20 h-20 mx-auto   rounded-full flex items-center justify-center mb-4`} />
      </div>

      <Body variant="title" className=" mt-10">
        {title}
      </Body>

     {message && <Body className="text-sm text-foreground/80   px-4">{message}</Body>}

       

      {children}
    </div>
  );
};

// Preset configurations
export const ErrorPaymentCard: FC<Omit<PaymentStatusCardProps, "status" | "children">> = (props) => (
  <PaymentStatusCard {...props} status="error">
    <div className="flex items-center w-[80%] mt-16 gap-3  pt-2">
      <Button variant="outline" onClick={props.onSecondaryClick} size="sm" className=" w-full">
        {props.secondaryButtonText}
      </Button>

      <Button onClick={props.onPrimaryClick} size="sm" className="w-full transition-colors">
        {props.primaryButtonText}
      </Button>
    </div>
  </PaymentStatusCard>
);

export const SuccessPaymentCard: FC<Omit<PaymentStatusCardProps, "status" | "children">> = (props) => (
  <PaymentStatusCard {...props} status="success">
    <div className="w-[95%] bg-[#FFF8EF] border border-orange-200 rounded-lg p-4 mt-10 text-left">
      <Body variant="body-large" className="text-sm font-semibold text-[#B46500]  mb-2">
        {props?.alertBox?.title || "Token generation failed"}
      </Body>
      <Body className=" leading-relaxed">{props?.alertBox?.message}</Body>
      {(props?.alertBox?.linkText || props?.alertBox?.linkHref) && (
        <Body className="text-sm text-foreground mt-2">
          If this persists: contact{" "}
          {props?.alertBox?.onLinkClick ? (
            <button onClick={props?.alertBox?.onLinkClick} className="text-blue-600 underline hover:text-blue-700">
              {props?.alertBox?.linkText}
            </button>
          ) : (
            <a
              href={props?.alertBox?.linkHref}
              className="text-foreground/80 underline hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
            >
              {props?.alertBox?.linkText}
            </a>
          )}
        </Body>
      )}
    </div>
    <div className="flex items-center w-[80%] mt-16 gap-3  pt-2">
      <Button variant="outline" onClick={props.onSecondaryClick} size="sm" className=" w-full">
        {props.secondaryButtonText}
      </Button>

      <Button onClick={props.onPrimaryClick} size="sm" className="w-full transition-colors">
        {props.primaryButtonText}
      </Button>
    </div>
  </PaymentStatusCard>
);

// export const WarningPaymentCard: FC<Omit<PaymentStatusCardProps, "status">> = (props) => (
//   <PaymentStatusCard {...props} status="warning" />
// );
